import type { StepChannel, WizardPipeline, PipelineResult, StepSignal } from './types';
import { waitForSignal } from './waitForSignal';

/**
 * Factory that creates composable pipeline helpers.
 * The handler uses these to request validation/submission from the step
 * through the channel without knowing the step's internals.
 *
 * IMPORTANT: Subscribe for the response signal BEFORE sending the command,
 * because the adapter may respond synchronously within send().
 */
export function createWizardPipeline(channel: StepChannel): WizardPipeline {
  const validateOnly = async (): Promise<PipelineResult> => {
    const signalPromise = waitForSignal(channel, [
      'VALIDATION_SUCCESS',
      'VALIDATION_FAILURE',
    ]);
    channel.send({ type: 'REQUEST_VALIDATE' });
    const signal = await signalPromise;

    if (signal.type === 'VALIDATION_FAILURE') {
      return {
        blocked: true,
        errors: (signal as Extract<StepSignal, { type: 'VALIDATION_FAILURE' }>).errors,
      };
    }

    return {
      blocked: false,
      data: (signal as Extract<StepSignal, { type: 'VALIDATION_SUCCESS' }>).values,
    };
  };

  const submitOnly = async (): Promise<PipelineResult> => {
    const signalPromise = waitForSignal(channel, [
      'SUBMIT_SUCCESS',
      'SUBMIT_FAILURE',
    ]);
    channel.send({ type: 'REQUEST_SUBMIT' });
    const signal = await signalPromise;

    if (signal.type === 'SUBMIT_FAILURE') {
      return {
        blocked: true,
        errors: { _submit: [String((signal as Extract<StepSignal, { type: 'SUBMIT_FAILURE' }>).error)] },
      };
    }

    return {
      blocked: false,
      data: (signal as Extract<StepSignal, { type: 'SUBMIT_SUCCESS' }>).result,
    };
  };

  const validateAndSubmit = async (): Promise<PipelineResult> => {
    const validateResult = await validateOnly();
    if (validateResult.blocked) return validateResult;
    return submitOnly();
  };

  const collectValues = async (): Promise<Record<string, unknown>> => {
    const signalPromise = waitForSignal(channel, ['VALUES_COLLECTED']);
    channel.send({ type: 'REQUEST_VALUES' });
    const signal = await signalPromise;
    return (signal as Extract<StepSignal, { type: 'VALUES_COLLECTED' }>).values;
  };

  return {
    validateAndSubmit,
    validateOnly,
    collectValues,
    submitOnly,
  };
}
